import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, CalendarRange } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { cmsApi, apiError, fmtPrice, fmtDate, mediaUrl } from "../api";
import { ImageUpload } from "../components/ImageUpload";
import { usePageData, PageTitle, Card, TextField, Toggle, ConfirmDialog, OrderButtons, moveItem, Spinner, num } from "../components/admin-ui";

const EMPTY = { title: "", description: "", image_media_id: null, original_price: "", promo_price: "", start_date: "", end_date: "", active: true, featured: false };

function status(p) {
  const today = new Date().toISOString().slice(0, 10);
  if (!p.active) return { label: "Inaktiv", cls: "bg-stone-100 text-stone-500" };
  if (p.start_date && p.start_date > today) return { label: "Geplant", cls: "bg-amber-100 text-amber-800" };
  if (p.end_date && p.end_date < today) return { label: "Abgelaufen", cls: "bg-red-50 text-red-700" };
  return { label: "Live", cls: "bg-emerald-50 text-emerald-700" };
}

function PromoDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Bitte einen Titel angeben"); return; }
    if (f.start_date && f.end_date && f.end_date < f.start_date) { toast.error("Enddatum liegt vor dem Startdatum"); return; }
    setSaving(true);
    try {
      const body = { title: f.title, description: f.description, image_media_id: f.image_media_id, original_price: num(f.original_price), promo_price: num(f.promo_price), start_date: f.start_date || null, end_date: f.end_date || null, active: f.active, featured: f.featured };
      if (f.id) await cmsApi.put(`/website-admin/promotions/${f.id}`, body); else await cmsApi.post("/website-admin/promotions", body);
      toast.success(f.id ? "Angebot aktualisiert" : "Angebot erstellt"); onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="promo-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{f.id ? "Angebot bearbeiten" : "Neues Angebot"}</DialogTitle><DialogDescription>Abgelaufene Angebote verschwinden automatisch von der Website.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-12 gap-4">
          <div className="sm:col-span-7 space-y-3">
            <TextField label="Titel" value={f.title} onChange={(v) => set("title", v)} testid="promo-title-input" />
            <TextField label="Beschreibung" value={f.description} onChange={(v) => set("description", v)} textarea rows={3} testid="promo-description-input" />
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Originalpreis (€)" type="number" step="0.5" min="0" value={f.original_price} onChange={(v) => set("original_price", v)} testid="promo-original-price-input" />
              <TextField label="Aktionspreis (€)" type="number" step="0.5" min="0" value={f.promo_price} onChange={(v) => set("promo_price", v)} testid="promo-price-input" />
              <TextField label="Start" type="date" value={f.start_date} onChange={(v) => set("start_date", v)} testid="promo-start-input" />
              <TextField label="Ende" type="date" value={f.end_date} onChange={(v) => set("end_date", v)} testid="promo-end-input" />
            </div>
          </div>
          <div className="sm:col-span-5 space-y-3">
            <ImageUpload value={f.image_media_id} onChange={(id) => set("image_media_id", id)} kind="promotion" label="Banner" testid="promo-image" />
            <Toggle label="Aktiv" checked={f.active} onChange={(v) => set("active", v)} testid="promo-active-toggle" />
            <Toggle label="Auf Startseite hervorheben" checked={f.featured} onChange={(v) => set("featured", v)} testid="promo-featured-toggle" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white" data-testid="promo-save">{saving ? "Speichern…" : "Speichern"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PromotionsAdmin() {
  const { data, loading, reload } = usePageData("/website-admin/promotions");
  const [dialog, setDialog] = useState(null);
  const [del, setDel] = useState(null);
  if (loading || !data) return <Spinner />;
  const reorder = async (i, dir) => { try { await cmsApi.put("/website-admin/promotions/reorder", { ids: moveItem(data, i, dir).map((p) => p.id) }); reload(); } catch (e) { toast.error(apiError(e)); } };
  const toggleActive = async (p) => {
    try { const { id, order, created_at, updated_at, updated_by, ...body } = p; await cmsApi.put(`/website-admin/promotions/${id}`, { ...body, active: !p.active }); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const confirmDelete = async () => { try { await cmsApi.delete(`/website-admin/promotions/${del.id}`); toast.success("Angebot gelöscht"); setDel(null); reload(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div data-testid="promotions-admin-page">
      <PageTitle title="Angebote" text="Aktionen mit Laufzeit, Preisen und Bannern."
        action={<Button onClick={() => setDialog(EMPTY)} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white" data-testid="add-promo-button"><Plus className="h-4 w-4 mr-1" /> Neues Angebot</Button>} />
      {data.length === 0 && <Card><p className="text-sm text-stone-500">Noch keine Angebote. Erstellen Sie Ihr erstes Angebot.</p></Card>}
      <div className="grid md:grid-cols-2 gap-4">
        {data.map((p, i) => {
          const st = status(p);
          return (
            <Card key={p.id} testid={`promo-row-${p.id}`} className="flex gap-4">
              <OrderButtons testid={`promo-${p.id}`} disableUp={i === 0} disableDown={i === data.length - 1} onUp={() => reorder(i, -1)} onDown={() => reorder(i, 1)} />
              <div className="h-20 w-28 rounded-lg bg-[#F1EAE0] overflow-hidden shrink-0">{p.image_media_id && <img src={mediaUrl(p.image_media_id)} alt="" className="h-full w-full object-cover" />}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`promo-status-${p.id}`}>{st.label}</span>{p.featured && <Star className="h-3.5 w-3.5 text-[#C59B6C] fill-[#C59B6C]" />}</div>
                <p className="font-serif text-lg text-stone-900 mt-1 truncate">{p.title}</p>
                <p className="text-xs text-stone-500 flex flex-wrap gap-x-3">
                  {(fmtPrice(p.promo_price) || fmtPrice(p.original_price)) && <span>{fmtPrice(p.promo_price)}{p.original_price != null && <span className="line-through ml-1 text-stone-400">{fmtPrice(p.original_price)}</span>}</span>}
                  {(p.start_date || p.end_date) && <span className="inline-flex items-center gap-1"><CalendarRange className="h-3 w-3" />{fmtDate(p.start_date)}{p.start_date && p.end_date ? " – " : ""}{fmtDate(p.end_date)}</span>}
                </p>
                <div className="flex items-center gap-1 mt-2">
                  <Toggle label="" checked={p.active} onChange={() => toggleActive(p)} testid={`promo-active-${p.id}`} />
                  <button onClick={() => setDialog({ ...EMPTY, ...p, original_price: p.original_price ?? "", promo_price: p.promo_price ?? "", start_date: p.start_date || "", end_date: p.end_date || "" })} className="p-2 text-stone-400 hover:text-[#4A1D24]" data-testid={`promo-edit-${p.id}`} aria-label="Bearbeiten"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => setDel(p)} className="p-2 text-stone-400 hover:text-red-700" data-testid={`promo-delete-${p.id}`} aria-label="Löschen"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      {dialog && <PromoDialog initial={dialog} onClose={() => setDialog(null)} onSaved={reload} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Angebot löschen?" text={`„${del?.title}“ wird dauerhaft entfernt.`} onConfirm={confirmDelete} testid="delete-dialog" />
    </div>
  );
}
